import { useState, useEffect, useCallback } from 'react';

const cache = {};
// cache[userId] = { data: Object, fetchedAt: number }

const inFlight = {};
// inFlight[userId] = Promise

// TTL: 5 minutes (metrics don't change frequently)
const METRICS_TTL_MS = 300000;

// Listeners: any mounted hook instance registers a forceUpdate here
const listeners = new Set();
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

async function fetchInfluencerData(userId, force = false) {
  const now = Date.now();
  const entry = cache[userId];

  if (!force && entry && entry.fetchedAt && now - entry.fetchedAt < METRICS_TTL_MS) {
    return entry.data;
  }

  // Dedupe: if a request is already in-flight for this userId, wait for it
  if (inFlight[userId]) {
    return inFlight[userId];
  }

  const promise = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/influencer/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        cache[userId] = { data, fetchedAt: Date.now() };
        return data;
      }
      throw new Error('Failed to fetch influencer metrics');
    } finally {
      delete inFlight[userId];
    }
  })();

  inFlight[userId] = promise;
  return promise;
}

/**
 * Shared hook for influencer metrics (completed_campaigns, etc.) from /api/influencer/:id.
 * Uses module-level cache keyed by userId so data is shared across all mounted components.
 *
 * @param {string|number} userId - The influencer user ID
 * @returns {{ completedCampaigns: number, profile: Object|null, loading: boolean, error: string|null, refresh: Function, lastUpdated: number }}
 */
/** Resets module cache (for tests and rare logout flows). */
export function resetInfluencerMetricsCache() {
  Object.keys(cache).forEach((key) => {
    delete cache[key];
  });
  Object.keys(inFlight).forEach((key) => {
    delete inFlight[key];
  });
}

export default function useInfluencerMetrics(userId) {
  const entry = cache[userId];
  const [profile, setProfile] = useState(entry ? entry.data : null);
  const [loading, setLoading] = useState(!entry || !entry.fetchedAt);
  const [error, setError] = useState(null);

  // Sync local state from module cache
  const syncFromCache = useCallback(() => {
    const cached = cache[userId];
    if (cached && cached.data) {
      setProfile(cached.data);
    }
  }, [userId]);

  // Register this instance as a listener
  useEffect(() => {
    listeners.add(syncFromCache);
    return () => {
      listeners.delete(syncFromCache);
    };
  }, [syncFromCache]);

  // Refresh function callable by consumers
  const refresh = useCallback(async (force = false) => {
    if (!userId) return;
    try {
      setError(null);
      const data = await fetchInfluencerData(userId, force);
      setProfile(data);
      notifyListeners();
    } catch (err) {
      setError(err.message || 'Failed to load influencer metrics.');
    }
  }, [userId]);

  // Initial fetch on mount or userId change
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const loadData = async () => {
      const cached = cache[userId];

      if (cached && cached.fetchedAt) {
        setProfile(cached.data);
        setLoading(false);

        // Refresh in background if stale
        const now = Date.now();
        if (now - cached.fetchedAt >= METRICS_TTL_MS) {
          try {
            await refresh(false);
          } catch {
            // Ignore background refresh errors
          }
        }
        return;
      }

      setLoading(true);
      try {
        const data = await fetchInfluencerData(userId, false);
        if (!cancelled) {
          setProfile(data);
          notifyListeners();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load influencer metrics.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => { // eslint-disable-line consistent-return
      cancelled = true;
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cached = cache[userId];

  return {
    completedCampaigns: profile ? (profile.completed_campaigns || 0) : 0,
    profile,
    loading,
    error,
    refresh,
    lastUpdated: cached ? cached.fetchedAt : 0,
  };
}
